<template>
  <button
    type="button"
    :class="buttonClasses"
    :title="buttonTitle"
    :aria-label="buttonTitle || undefined"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <component
      v-if="iconComponent && displayMode !== 'text-only'"
      :is="iconComponent"
      class="btn-icon"
      :class="{ 'btn-icon-loading': loading }"
    />
    <span
      v-if="displayMode !== 'icon-only'"
      class="btn-label"
    >
      {{ buttonText }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { businessButtonConfig, type BusinessButtonType } from '@/config/button.config'
import { Loading } from '@element-plus/icons-vue'

interface Props {
  // 业务类型（自动配置图标、文本、颜色）
  action?: BusinessButtonType
  // 尺寸
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  // 宽度
  width?: 'auto' | 'single' | 'double'
  // 显示模式
  displayMode?: 'icon-text' | 'text-only' | 'icon-only'
  // 自定义颜色（覆盖业务配置）
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'default' | 'info' | 'primary-light' | 'success-light' | 'warning-light' | 'danger-light' | 'default-light' | 'info-light'
  // 自定义文本：
  // - 如果指定了 action，customText 会覆盖配置文本
  // - 如果未指定 action，customText 为必须或为空
  customText?: string
  // 自定义图标（覆盖业务配置）
  customIcon?: Component
  // 禁用状态
  disabled?: boolean
  // 加载状态
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  width: 'auto',
  displayMode: 'icon-text',
  disabled: false,
  loading: false
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const { t } = useI18n()
const iconComponent = computed<Component | null>(() => {
  if (props.loading) {
    return Loading
  }

  if (props.customIcon) {
    return props.customIcon
  }

  if (props.action) {
    const config = businessButtonConfig[props.action]
    return config?.icon ?? null
  }

  return null
})

const isDisabled = computed(() => props.disabled || props.loading)

// 按钮类名
const buttonClasses = computed(() => {
  const classes = ['btn-standard']
  
  // 尺寸
  classes.push(`btn-size-${props.size}`)
  
  // 宽度
  classes.push(`btn-width-${props.width}`)
  
  // 显示模式
  if (props.displayMode === 'icon-only') {
    classes.push('btn-icon-only')
  } else if (props.displayMode === 'text-only') {
    classes.push('btn-text-only')
  }
  
  // 颜色 - 自定义颜色优先，否则使用业务配置
  if (props.color) {
    classes.push(`btn-color-${props.color}`)
  } else if (props.action) {
    const config = businessButtonConfig[props.action]
    if (config) {
      classes.push(config.colorClass)
    }
  } else {
    // 默认颜色
    classes.push('btn-color-default')
  }
  
  return classes
})

// 按钮文本
const buttonText = computed(() => {
  // 如果有自定义文本，优先使用
  if (props.customText) {
    return props.customText
  }
  
  // 如果指定了业务类型，使用配置文本
  if (props.action) {
    const config = businessButtonConfig[props.action]
    if (config) {
      return t(config.i18nKey)
    }
  }
  
  // 都没有则返回空
  return ''
})

const buttonTitle = computed(() => buttonText.value || '')

// 点击处理
const handleClick = (event: MouseEvent) => {
  if (!isDisabled.value) {
    emit('click', event)
  }
}
</script>

<style scoped lang="scss">
// 组件级样式（如果需要）
</style>
